
import { useStore } from '@/store'
import * as THREE from 'three'
import { defineComponent, inject, onUnmounted } from 'vue'
import { computed, watch } from 'vue'
import MaterialCreateService from '@/services/Three/material-create.service'
import { pocketFragmentShaderString } from '@/shaders/pocketFragment'
import References from '@/singletons/references'
import pocketSettings from '@/settings/pocket-settings.json'
import TextureLoadService from '@/services/Three/texture-load.service'
import SentrySingleton from '@/singletons/sentry-singleton'

export default defineComponent({
  name: 'ThreeRandselPocket',
  setup() {
    const store = useStore()
    const emitter: any = inject('emitter')
    const isBagLoaded = computed(() => store.getters.isBagLoaded)
    const isSceneLoaded = computed(() => store.getters.isSceneLoaded)
    const configPocketId = computed(() => store.getters.configPocketId)
    const isRainbowConfig = computed(() => store.getters.isRainbowConfig)
    const configThreadId = computed(() => store.getters.configThreadId)
    const isRenderer = inject('isRenderer') as boolean
    const materialCreateService: MaterialCreateService = new MaterialCreateService()
    let textureLoadService: TextureLoadService
    let currentTexture = ''
    let currentItoTexture = ''
    let currentNormal = ''

    emitter.on('updateConfig', updateOption)

    // prettier-ignore
    watch(() => isSceneLoaded.value, (_) => {
        try {
          initTextureLoadService()
          let mat = materialCreateService.createMaterial(pocketFragmentShaderString, true, false, isRenderer)
          mat.uniforms.itoColor = { value: materialCreateService.createColor(0xFFFFFF) };
          mat.uniforms.pocketTexture = { value: null };
          mat.uniforms.pocketItoTexture = { value: null };
          mat.uniforms.zipperColor = { value: materialCreateService.createColor(0xFFFFFF) };
          mat.uniforms.zipperTexture = { value: null };
          textureLoadService.loadImage('default/zipper', function(tex) {
              mat.uniforms.zipperTexture.value = tex;
          })
          mat.uniforms.normalScale.value = materialCreateService.getNormalMapScale()
          mat.uniforms.roughness.value = materialCreateService.getRoughness()
          mat.uniforms.metalness.value = materialCreateService.getMetalness()
          References.instance.setPocketMaterial(mat)
        }
        catch(err) {
          SentrySingleton.instance.captureException(err)
        }
      }
    )

    function initTextureLoadService() {
      if (!textureLoadService || textureLoadService == undefined) {
        textureLoadService = new TextureLoadService(References.instance.getSceneReferences().maxAnisotropy)
      }
    }

    // prettier-ignore
    watch(() => isBagLoaded.value, (_) => { References.instance.getBagReferences().pocket.material = References.instance.getPocketMaterial() })
    // prettier-ignore
    watch(() => configPocketId.value, (_) => { updateOption() })

    function updateOption() {
      try {
        if (configPocketId.value) {
          setPocket(pocketSettings.pocketTextures.find((x: any) => x.id == configPocketId.value))
        } else {
          setPocket(pocketSettings.pocketTextures.find((x: any) => x.default && x.default === true))
        }
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function setPocket(obj: any) {
      initTextureLoadService()
      loadTexture(obj ? obj.texture : null)
      loadItoTexture(obj ? (isRainbowConfig.value && obj.rainbowTexture ? obj.rainbowTexture : obj.itoTexture) : null)
      loadNormal(obj ? obj.normal : null)
    }

    function loadTexture(path: string | null) {
      if (path) {
        if (path != currentTexture) {
          currentTexture = path
          textureLoadService.loadImage(path, (tex: THREE.Texture | null) => {
            References.instance.getPocketMaterial().uniforms.pocketTexture = { value: tex }
          })
        }
      } else {
        References.instance.getPocketMaterial().uniforms.pocketTexture = { value: null }
      }
    }

    function loadItoTexture(path: string | null) {
      if (path) {
        if (path != currentItoTexture) {
          currentItoTexture = path
          textureLoadService.loadImage(path, (tex: THREE.Texture | null) => {
            References.instance.getPocketMaterial().uniforms.pocketItoTexture = { value: tex }
          })
        }
      } else {
        References.instance.getPocketMaterial().uniforms.pocketItoTexture = { value: null }
      }
    }

    function loadNormal(path: string | null | undefined) {
      const desiredPath = path ?? 'pocket/default_n'
      if (desiredPath != currentNormal) {
        currentNormal = desiredPath
        textureLoadService.loadImage(currentNormal, (tex: THREE.Texture | null) => {
          References.instance.getPocketMaterial().uniforms.normalMap.value = tex
        })
      }
    }

    // prettier-ignore
    watch(() => configThreadId.value, (_) => {
        try {
          if(configPocketId.value) {
            setPocket(pocketSettings.pocketTextures.find((x: any) => x.id == configPocketId.value))
          }
        }
        catch(err) {
          SentrySingleton.instance.captureException(err)
        }
      }
    )

    onUnmounted(() => {
      emitter.off('updateConfig', updateOption)
    })

    return {
      isBagLoaded,
      isSceneLoaded,

      configPocketId,
      isRainbowConfig,
      configThreadId
    }
  }
})
